import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Divider,
} from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { getTicketByPublicToken } from "../../Services/supportTicketsService";
import StatusChip from "./StatusChip";
import PriorityChip from "./PriorityChip";

interface TicketData {
  ticketNumber: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  type: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  closedAt?: string;
  createdBy?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}
// TODO: FIX this to handle preview of tickets now is not having it is returning 401 as ticket isn't matching, so this should be now fixed on backend to make sure token is accessible on backend
const TicketPreviewPage: React.FC = () => {
  const { token } = useParams<{ token: string; }>();
  const [ticket, setTicket] = useState<TicketData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTicket = async () => {
      if (!token) {
        setError(Vocabulary.invalidOrExpiredToken);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getTicketByPublicToken(token);
        setTicket(response.data.data);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching ticket:", err);

        // Handle specific error cases
        if (err.response?.status === 401) {
          setError(Vocabulary.invalidOrExpiredToken);
        } else if (err.response?.status === 404) {
          setError(Vocabulary.ticketNotFound);
        } else if (err.response?.data?.message) {
          setError(err.response.data.message);
        } else {
          setError(Vocabulary.invalidOrExpiredToken);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTicket();
  }, [token]);

  const formatDate = (dateString: string) => {
    if (!dateString) return Vocabulary.notAvailable;
    return new Date(dateString).toLocaleString("ro-RO", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, textAlign: "center" }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3 }}>
          {Vocabulary.loadingTicket}
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          {error === Vocabulary.invalidOrExpiredToken && (
            <>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {Vocabulary.ticketLinkExpired}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Vocabulary.contactSupport}
              </Typography>
            </>
          )}
          {error === Vocabulary.unauthorizedAccess && (
            <Typography variant="body1">
              {Vocabulary.notAuthorized}
            </Typography>
          )}
        </Paper>
      </Container>
    );
  }

  if (!ticket) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="warning">{Vocabulary.ticketNotFound}</Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3, textAlign: "center" }}>
          <Typography variant="h4" gutterBottom fontWeight="600">
            {Vocabulary.ticketPreview}
          </Typography>
          <Typography variant="h6" color="primary" gutterBottom>
            {ticket.ticketNumber}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Title */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom fontWeight="600">
            {ticket.title}
          </Typography>
        </Box>

        {/* Status, Priority, Type */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {Vocabulary.status}:
            </Typography>
            <StatusChip status={ticket.status} size="medium" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {Vocabulary.priority}:
            </Typography>
            <PriorityChip priority={ticket.priority} size="medium" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {Vocabulary.type}:
            </Typography>
            <Typography variant="body1" fontWeight="500">
              {Vocabulary[ticket.type] || ticket.type}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Description */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom fontWeight="600">
            {Vocabulary.description}:
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              backgroundColor: "#f9f9f9",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            <Typography variant="body1">{ticket.description}</Typography>
          </Paper>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Created By */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom fontWeight="600">
            {Vocabulary.createdBy}:
          </Typography>
          <Typography variant="body1">
            {ticket.createdBy?.firstName || ""} {ticket.createdBy?.lastName || ""}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {ticket.createdBy?.email || ""}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Timestamps */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom fontWeight="600">
              {Vocabulary.createdAt}:
            </Typography>
            <Typography variant="body1">{formatDate(ticket.createdAt)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom fontWeight="600">
              {Vocabulary.updatedAt}:
            </Typography>
            <Typography variant="body1">{formatDate(ticket.updatedAt)}</Typography>
          </Grid>
          {ticket.resolvedAt && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" gutterBottom fontWeight="600">
                {Vocabulary.resolvedAt}:
              </Typography>
              <Typography variant="body1">{formatDate(ticket.resolvedAt)}</Typography>
            </Grid>
          )}
          {ticket.closedAt && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" gutterBottom fontWeight="600">
                {Vocabulary.closedAt}:
              </Typography>
              <Typography variant="body1">{formatDate(ticket.closedAt)}</Typography>
            </Grid>
          )}
        </Grid>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #e0e0e0" }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {Vocabulary.automatedNotificationFromCRM}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default TicketPreviewPage;
